"use client"

import { HeaderSearchSpotlightItem } from "./HeaderSearchSpotlightItem"
import { HeaderSearchSpotlightActions } from "./HeaderSearchSpotlightActions"
import { HeaderSearchSpotlightDevActions, shouldShowStatsSampleDataDevActions } from "./HeaderSearchSpotlightDevActions"
import { HeaderSearchSpotlightSequenceSimDevActions } from "./HeaderSearchSpotlightSequenceSimDevActions"
import { useSearchContacts } from "@/hooks/dynamo/useSearchContacts"
import { chatStore$ } from "@/lib/ContactsMap"
import { fetchMapKeyData } from "@/sync/sync"
import { shouldShowSequenceSimulationDevActions } from "@/features/PowerDialer/dev/sequenceCallSimulationDev.helpers"
import { batch } from "@legendapp/state"
import { use$ } from "@legendapp/state/react"
import { Group, Loader, Text } from "@mantine/core"
import { useDebouncedValue } from "@mantine/hooks"
import { createSpotlight, Spotlight } from "@mantine/spotlight"
import { IconSearch } from "@tabler/icons-react"
import { useTranslations } from "next-intl"
import { useCallback, useEffect, useMemo, useState } from "react"
import { usePathname } from "next/navigation"
import { parsePhoneNumberWithError } from "libphonenumber-js"
import classes from "./HeaderSearchSpotlight.module.css"

export const [headerSearchStore, headerSearchSpotlight] = createSpotlight()

// Helper to count digits in a string
function countDigits(str: string): number {
  return (str.match(/\d/g) || []).length
}

export function HeaderSearchSpotlight() {
  const t = useTranslations("contactsSearchInput")
  const pathname = usePathname()
  const isMessengerPage = pathname === "/messenger"

  const [query, setQuery] = useState("")
  const [debouncedSearchInputValue] = useDebouncedValue(query, 500)

  const cleanQueryInput = useCallback((query: string) => {
    // 1. Detect if it is a phone number
    try {
      const phoneNumber = parsePhoneNumberWithError(query, {
        defaultCountry: "US",
      })

      return phoneNumber.formatInternational().replace(/\s/g, "")
    } catch {
      // It is not a phone number
    }

    // 2. If it is not, search it as is
    return query.trim()
  }, [])

  const [searchQueryData, searchQuery] = useSearchContacts({
    isDisabled: !debouncedSearchInputValue.trim() || debouncedSearchInputValue.length < 3,
    beginsWith: cleanQueryInput(debouncedSearchInputValue),
    type: "all",
  })

  useEffect(() => {
    if (searchQueryData) {
      const queueOfContactsToFetchFromSync: Array<string> = []
      batch(() => {
        const ids: Array<string> = []
        searchQueryData.forEach((contact) => {
          // Skip contacts without a valid ContactPersonId
          if (!contact.ContactPersonId) {
            return
          }

          if (!chatStore$.contacts[contact.ContactPersonId].peek()) {
            chatStore$.contacts[contact.ContactPersonId].set(contact)
          }

          ids.push(contact.ContactPersonId)
          queueOfContactsToFetchFromSync.push(contact.ContactPersonId)
        })

        chatStore$.searchChats.set(ids)
      })

      queueOfContactsToFetchFromSync.forEach((contactID) => {
        fetchMapKeyData({
          key: contactID,
          map: "contacts",
        })
      })
    }
  }, [searchQueryData])

  const foundContacts = use$(() => {
    const contacts = (chatStore$.searchChats.get() ?? []).map((chat) => chatStore$.contacts[chat].get()).filter((contact) => !!contact && !!contact.ContactPersonId)

    // On non-messenger pages, only show contacts with phone numbers (callable)
    if (!isMessengerPage) {
      return contacts.filter((contact) => (contact.ContactPhoneNumber && contact.ContactPhoneNumber.length > 0) || (contact.ContactPhoneNumbers && contact.ContactPhoneNumbers.length > 0))
    }

    return contacts
  })

  const items = useMemo(
    () => foundContacts.map((item, index) => <HeaderSearchSpotlightItem key={item.ContactPersonId || `contact-${index}`} item={item} isMessengerPage={isMessengerPage} searchQuery={debouncedSearchInputValue} />),
    [foundContacts, isMessengerPage, debouncedSearchInputValue]
  )

  // Check if input has 7+ digits (for showing actions)
  const hasEnoughDigits = useMemo(() => countDigits(debouncedSearchInputValue) >= 7, [debouncedSearchInputValue])
  const hasStatsSampleDevActions = useMemo(() => shouldShowStatsSampleDataDevActions(debouncedSearchInputValue), [debouncedSearchInputValue])
  const hasSequenceSimulationDevActions = useMemo(() => shouldShowSequenceSimulationDevActions(debouncedSearchInputValue), [debouncedSearchInputValue])

  const renderItems = useMemo(() => {
    if (searchQuery.isFetching) {
      return (
        <Spotlight.Empty>
          <Group w="100%" justify="center" align="center">
            <Loader color="blue" size="xs" />
            <Text size="sm">{t("searching")}</Text>
          </Group>
        </Spotlight.Empty>
      )
    }

    // If we have results or actions to show
    if (items.length > 0 || hasEnoughDigits || hasStatsSampleDevActions || hasSequenceSimulationDevActions) {
      return (
        <Spotlight.ActionsList className={classes.actionsList}>
          {hasStatsSampleDevActions && <HeaderSearchSpotlightDevActions />}
          {hasSequenceSimulationDevActions && <HeaderSearchSpotlightSequenceSimDevActions query={debouncedSearchInputValue} />}
          {hasEnoughDigits && <HeaderSearchSpotlightActions query={debouncedSearchInputValue} isMessengerPage={isMessengerPage} />}
          {items}
        </Spotlight.ActionsList>
      )
    }

    // No results, no enough digits
    if (debouncedSearchInputValue.trim().length > 0) {
      return (
        <Spotlight.Empty>
          <Text size="sm">{t("mode.search.title")}</Text>
        </Spotlight.Empty>
      )
    }

    return []
  }, [debouncedSearchInputValue, items, searchQuery.isFetching, t, hasEnoughDigits, hasStatsSampleDevActions, hasSequenceSimulationDevActions, isMessengerPage])

  return (
    <Spotlight.Root
      store={headerSearchStore}
      shortcut={["mod + K"]}
      query={query}
      onQueryChange={(query: string) => {
        setQuery(query)
      }}
    >
      <Spotlight.Search
        classNames={{
          input: classes.input,
        }}
        autoComplete="off"
        placeholder={t("input.placeholder")}
        leftSection={<IconSearch stroke={1.5} />}
      />
      {debouncedSearchInputValue.trim().length !== 0 ? renderItems : null}
    </Spotlight.Root>
  )
}
